/** Structural schedule shared by legacy slot settings and template snapshots. */
export interface SlotSchedule {
  startDate?: any;
  endDate?: any;
  isMondayOpened?: boolean;
  mondayStartTime?: string | null;
  mondayEndTime?: string | null;
  isTuesdayOpened?: boolean;
  tuesdayStartTime?: string | null;
  tuesdayEndTime?: string | null;
  isWednesdayOpened?: boolean;
  wednesdayStartTime?: string | null;
  wednesdayEndTime?: string | null;
  isThursdayOpened?: boolean;
  thursdayStartTime?: string | null;
  thursdayEndTime?: string | null;
  isFridayOpened?: boolean;
  fridayStartTime?: string | null;
  fridayEndTime?: string | null;
  isSaturdayOpened?: boolean;
  saturdayStartTime?: string | null;
  saturdayEndTime?: string | null;
  isSundayOpened?: boolean;
  sundayStartTime?: string | null;
  sundayEndTime?: string | null;
  exceptions?: SlotScheduleException[];
  periods?: { startDate: any; endDate: any; settings: SlotSchedule }[];
}

export interface SlotScheduleException {
  date: any;
  isClosed: boolean;
  startTime: string | null;
  endTime: string | null;
  isPrivatized?: boolean;
  promotion?: { isEnabled?: boolean; percentage?: number; label?: Record<string, string>; message?: Record<string, string> };
}

/** Reads a calendar date without shifting ISO strings across time zones. */
export function getSlotCalendarDate(value: any): string | null {
  if (value === null || value === undefined || value === '')
    return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const key = value.substring(0, 10);
    const date = new Date(`${key}T00:00:00Z`);
    return !isNaN(date.getTime()) && date.toISOString().substring(0, 10) === key ? key : null;
  }
  if (typeof value?.toDate === 'function')
    return getSlotCalendarDate(value.toDate());
  if (typeof value?.seconds === 'number')
    return getSlotCalendarDate(new Date(value.seconds * 1000));
  if (typeof value?._seconds === 'number')
    return getSlotCalendarDate(new Date(value._seconds * 1000));
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString().substring(0, 10);
}

/** Preserves the local calendar day of browser selections without changing stored UTC dates. */
export function getSlotSelectedCalendarDate(value: Date | string): string | null {
  if (typeof value === 'string')
    return getSlotCalendarDate(value);
  if (isNaN(value.getTime()))
    return null;
  const year = value.getFullYear();
  const month = (value.getMonth() + 1).toString().padStart(2, '0');
  const day = value.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Creates a local calendar date from an Ionic date selection, regardless of its ISO time suffix. */
export function parseSlotCalendarDate(value: string): Date | null {
  const dateKey = getSlotCalendarDate(value);
  if (!dateKey)
    return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Counts calendar days rather than elapsed hours so availability indices survive daylight-saving changes. */
export function getSlotCalendarDayIndex(date: Date | string, today: Date = new Date()): number {
  const dateKey = getSlotSelectedCalendarDate(date);
  const todayKey = getSlotSelectedCalendarDate(today);
  if (!dateKey || !todayKey)
    return -1;
  return Math.round((Date.parse(dateKey) - Date.parse(todayKey)) / (24 * 60 * 60 * 1000));
}

/** Resolves inclusive periods, with ticket exceptions taking precedence over template exceptions. */
export function resolveSlotSettingsForDate<T extends SlotSchedule>(date: Date | string, settings?: T): T | undefined {
  if (!settings)
    return undefined;
  const dateKey = getSlotSelectedCalendarDate(date);
  if (!dateKey)
    return settings;
  const period = settings.periods?.find((candidate) => {
    const start = getSlotCalendarDate(candidate.startDate);
    const end = getSlotCalendarDate(candidate.endDate);
    return !!start && !!end && dateKey >= start && dateKey <= end;
  });
  const ticketExceptions = (settings.exceptions || []).filter((exception) => getSlotCalendarDate(exception.date) === dateKey);
  const periodExceptions = (period?.settings?.exceptions || []).filter((exception) => getSlotCalendarDate(exception.date) === dateKey);
  return {
    ...settings,
    ...period?.settings,
    startDate: settings.startDate,
    endDate: settings.endDate,
    exceptions: ticketExceptions.length > 0 ? ticketExceptions : periodExceptions
  };
}

/** Returns only the effective exceptions for the requested calendar date. */
export function getSlotExceptionsForDate(date: Date | string, settings?: SlotSchedule): SlotScheduleException[] {
  return resolveSlotSettingsForDate(date, settings)?.exceptions || [];
}

/** Checks the global calendar bounds before applying exceptions and weekly opening days. */
export function isDateOpenForSlotSettings(date: Date | string, settings?: SlotSchedule): boolean {
  if (!settings)
    return true;
  const dateKey = getSlotSelectedCalendarDate(date);
  if (!dateKey)
    return false;
  const start = getSlotCalendarDate(settings.startDate);
  const end = getSlotCalendarDate(settings.endDate);
  if ((start && dateKey < start) || (end && dateKey > end))
    return false;
  const effective = resolveSlotSettingsForDate(date, settings)!;
  const exceptions = effective.exceptions || [];
  if (exceptions.length > 0)
    return exceptions.every((exception) => !exception.isClosed);
  const weekdays = [effective.isSundayOpened, effective.isMondayOpened, effective.isTuesdayOpened,
    effective.isWednesdayOpened, effective.isThursdayOpened, effective.isFridayOpened, effective.isSaturdayOpened];
  return !!weekdays[new Date(`${dateKey}T12:00:00Z`).getUTCDay()];
}

/** Returns effective opening windows, including multiple exceptions on the same date. */
export function getDateTimeWindowsForSlot(date: Date | string, settings?: SlotSchedule): { startTime: string | null; endTime: string | null }[] {
  if (!settings)
    return [{ startTime: null, endTime: null }];
  if (!isDateOpenForSlotSettings(date, settings))
    return [{ startTime: null, endTime: null }];
  const effective = resolveSlotSettingsForDate(date, settings)!;
  const startTimes = [effective.sundayStartTime, effective.mondayStartTime, effective.tuesdayStartTime,
    effective.wednesdayStartTime, effective.thursdayStartTime, effective.fridayStartTime, effective.saturdayStartTime];
  const endTimes = [effective.sundayEndTime, effective.mondayEndTime, effective.tuesdayEndTime,
    effective.wednesdayEndTime, effective.thursdayEndTime, effective.fridayEndTime, effective.saturdayEndTime];
  const weekday = new Date(`${getSlotSelectedCalendarDate(date)}T12:00:00Z`).getUTCDay();
  const fallbackStart = startTimes[weekday] || null;
  const fallbackEnd = endTimes[weekday] || null;
  return effective.exceptions?.length
    ? effective.exceptions.map((exception) => ({ startTime: exception.startTime || fallbackStart, endTime: exception.endTime || fallbackEnd }))
    : [{ startTime: fallbackStart, endTime: fallbackEnd }];
}
